import type { ThemeConfig } from "@/features/auth/types/organization.type";

const HEX_COLOR_REGEX = /^#[0-9A-Fa-f]{6}$/;
const DEFAULT_DARKEN_FACTOR = 0.85;
const DEFAULT_LIGHT_ALPHA = 0.1255;

type RgbColor = { r: number, g: number, b: number };

export const hexToRgb = (hex: string): RgbColor | null => {
  if (!HEX_COLOR_REGEX.test(hex)) return null;

  const cleanHex = hex.replace("#", "");
  return {
    r: parseInt(cleanHex.substring(0, 2), 16),
    g: parseInt(cleanHex.substring(2, 4), 16),
    b: parseInt(cleanHex.substring(4, 6), 16),
  };
};

export const darkenHex = (hex: string, factor: number = DEFAULT_DARKEN_FACTOR): string => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const darken = (channel: number): number => Math.round(channel * factor);
  const toHex = (value: number): string => value.toString(16).padStart(2, "0").toUpperCase();

  return `#${toHex(darken(rgb.r))}${toHex(darken(rgb.g))}${toHex(darken(rgb.b))}`;
};

export const lightenHexWithAlpha = (hex: string, alpha: number = DEFAULT_LIGHT_ALPHA): string => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
};

export const BRAND_COLOR_CSS_VARIABLES: Record<string, string[]> = {
  primary: [
    "--brand-primary",
    "--brand-primary-emphasis",
    "--brand-lightprimary",
    "--brand-darkprimary",
    "--sidebar-primary",
    "--sidebar-ring",
  ],
  secondary: [
    "--brand-secondary",
    "--brand-secondary-emphasis",
    "--brand-lightsecondary",
    "--brand-darksecondary",
  ],
};

const generateVariablesForColor = (
  colorKey: string,
  hex: string,
): Array<[string, string]> => {
  const variables = BRAND_COLOR_CSS_VARIABLES[colorKey];
  if (!variables) return [];

  return variables.map((variableName) => {
    if (variableName.includes("emphasis")) {
      return [variableName, darkenHex(hex)];
    }
    if (variableName.includes("dark")) {
      return [variableName, darkenHex(hex, 0.4)];
    }
    if (variableName.includes("light")) {
      return [variableName, lightenHexWithAlpha(hex)];
    }
    return [variableName, hex];
  });
};

export const generateBrandColorVariables = (
  themeConfig: ThemeConfig | null | undefined,
): Array<[string, string]> => {
  if (!themeConfig?.colors) return [];

  const variables: Array<[string, string]> = [];
  const { colors } = themeConfig;

  if (colors.primary && HEX_COLOR_REGEX.test(colors.primary)) {
    variables.push(...generateVariablesForColor("primary", colors.primary));
  }

  if (colors.secondary && HEX_COLOR_REGEX.test(colors.secondary)) {
    variables.push(...generateVariablesForColor("secondary", colors.secondary));
  }

  return variables;
};

export const applyBrandColors = (themeConfig: ThemeConfig | null | undefined): void => {
  const variables = generateBrandColorVariables(themeConfig);
  const style = document.documentElement.style;

  for (const [name, value] of variables) {
    style.setProperty(name, value);
  }
};

export const resetBrandColors = (): void => {
  const style = document.documentElement.style;
  const allVariableNames = Object.values(BRAND_COLOR_CSS_VARIABLES).flat();

  for (const name of allVariableNames) {
    style.removeProperty(name);
  }
};
